// `date` fields are RFC 3339 strings. Email content (subject, snippet) is
// untrusted user input and must render as text, never markup.

export interface EmailListMessage {
  id: string;
  threadId: string;
  from: string;
  subject: string;
  snippet: string;
  date: string;
  unread?: boolean;
}

export interface EmailListPayload {
  type: "email_list";
  query?: string;
  messages: EmailListMessage[];
  totalResults: number;
}

interface EmailThreadAttachment {
  filename: string;
  mimeType: string;
  attachmentId: string;
  size: number;
}

export interface EmailThreadMessage {
  id: string;
  from: string;
  to: string[];
  cc?: string[];
  date: string;
  snippet: string;
  webLink?: string;
  attachments: EmailThreadAttachment[];
}

export interface EmailThreadPayload {
  type: "email_thread";
  threadId: string;
  subject: string;
  messages: EmailThreadMessage[];
}

export interface EmailDraftPayload {
  type: "email_draft";
  draftId: string;
  to: string[];
  cc?: string[];
  subject: string;
  snippet: string;
  webLink: string;
  updated: boolean;
}

const isString = (v: unknown): v is string => typeof v === "string";
const isNumber = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v);
const isStringArray = (v: unknown): v is string[] => Array.isArray(v) && v.every(isString);

const isEmailListMessage = (v: unknown): v is EmailListMessage => {
  if (!v || typeof v !== "object") return false;
  const r = v as Record<string, unknown>;
  return (
    isString(r.id) &&
    isString(r.threadId) &&
    isString(r.from) &&
    isString(r.subject) &&
    isString(r.snippet) &&
    isString(r.date) &&
    (r.unread === undefined || typeof r.unread === "boolean")
  );
};

const isEmailThreadAttachment = (v: unknown): v is EmailThreadAttachment => {
  if (!v || typeof v !== "object") return false;
  const r = v as Record<string, unknown>;
  return isString(r.filename) && isString(r.mimeType) && isString(r.attachmentId) && isNumber(r.size);
};

const isEmailThreadMessage = (v: unknown): v is EmailThreadMessage => {
  if (!v || typeof v !== "object") return false;
  const r = v as Record<string, unknown>;
  return (
    isString(r.id) &&
    isString(r.from) &&
    isStringArray(r.to) &&
    (r.cc === undefined || isStringArray(r.cc)) &&
    isString(r.date) &&
    isString(r.snippet) &&
    (r.webLink === undefined || isString(r.webLink)) &&
    (r.attachments === undefined || (Array.isArray(r.attachments) && r.attachments.every(isEmailThreadAttachment)))
  );
};

const normalizeThreadMessage = (m: EmailThreadMessage): EmailThreadMessage => ({
  ...m,
  attachments: Array.isArray(m.attachments) ? m.attachments : [],
});

// A missing `query` is tolerated.
export const parseEmailListPayload = (v: unknown): EmailListPayload | null => {
  if (!v || typeof v !== "object") return null;
  const r = v as Record<string, unknown>;
  if (r.type !== "email_list") return null;
  if (!Array.isArray(r.messages) || !r.messages.every(isEmailListMessage)) return null;
  if (!isNumber(r.totalResults)) return null;
  if (r.query !== undefined && !isString(r.query)) return null;
  return {
    type: "email_list",
    query: r.query,
    messages: r.messages,
    totalResults: r.totalResults,
  };
};

export const parseEmailThreadPayload = (v: unknown): EmailThreadPayload | null => {
  if (!v || typeof v !== "object") return null;
  const r = v as Record<string, unknown>;
  if (r.type !== "email_thread") return null;
  if (!isString(r.threadId)) return null;
  if (!isString(r.subject)) return null;
  if (!Array.isArray(r.messages) || !r.messages.every(isEmailThreadMessage)) return null;
  return {
    type: "email_thread",
    threadId: r.threadId,
    subject: r.subject,
    messages: r.messages.map(normalizeThreadMessage),
  };
};

// A missing `cc` is tolerated.
export const parseEmailDraftPayload = (v: unknown): EmailDraftPayload | null => {
  if (!v || typeof v !== "object") return null;
  const r = v as Record<string, unknown>;
  if (r.type !== "email_draft") return null;
  if (!isString(r.draftId)) return null;
  if (!isStringArray(r.to)) return null;
  if (r.cc !== undefined && !isStringArray(r.cc)) return null;
  if (!isString(r.subject)) return null;
  if (!isString(r.snippet)) return null;
  if (!isString(r.webLink)) return null;
  if (r.updated !== undefined && typeof r.updated !== "boolean") return null;
  return {
    type: "email_draft",
    draftId: r.draftId,
    to: r.to,
    cc: r.cc,
    subject: r.subject,
    snippet: r.snippet,
    webLink: r.webLink,
    updated: r.updated === true,
  };
};
